/**
 * Company Goal hooks — backend-persisted goal for the "Goal of the Mountain"
 * widget. The goal is stored on gmb-server and shared across every viewer of
 * the company (Company View, TV Leaderboard, etc.).
 *
 * Goal shape: { kpiId?, target?, period?, mountainImage? }
 */
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  GET_COMPANY_GOAL,
  SAVE_COMPANY_GOAL,
  DELETE_COMPANY_GOAL,
  UPLOAD_COMPANY_GOAL_MOUNTAIN_IMAGE,
  DELETE_COMPANY_GOAL_MOUNTAIN_IMAGE,
} from "../api/companyGoal";

const GOAL_CHANGE_EVENT = "gmb-company-goal-change";

export const normalizeCompanyGoal = (g) => {
  if (!g || typeof g !== "object") return null;
  const { kpiId, target, period, mountainImage } = g;
  const image = typeof mountainImage === "string" && mountainImage.trim()
    ? mountainImage.trim()
    : undefined;
  const hasMetrics = kpiId && Number.isFinite(Number(target)) && Number(target) > 0;
  if (!hasMetrics && !image) return null;
  return {
    ...(hasMetrics && {
      kpiId,
      target: Number(target),
      period: period || "month",
    }),
    ...(image && { mountainImage: image }),
  };
};

const broadcastGoal = (companyId, goal) => {
  window.dispatchEvent(
    new CustomEvent(GOAL_CHANGE_EVENT, {
      detail: { companyId, goal },
    }),
  );
};

/**
 * Reads the company goal from the backend and returns [goal, setGoal, uploadMountainImage, removeMountainImage].
 */
export const useCompanyGoal = () => {
  const company = useSelector((state) => state.companyReducer?.company);
  const companyId = company?._id || company?.id;
  const [goal, setGoalState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!companyId) {
      setGoalState(null);
      return () => {};
    }
    GET_COMPANY_GOAL()
      .then((res) => {
        if (cancelled) return;
        setGoalState(normalizeCompanyGoal(res?.data));
      })
      .catch(() => {
        if (cancelled) return;
        setGoalState(null);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    const onChange = (e) => {
      if (e?.detail?.companyId === companyId) setGoalState(e.detail.goal);
    };
    window.addEventListener(GOAL_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(GOAL_CHANGE_EVENT, onChange);
  }, [companyId]);

  const setGoal = useCallback(
    async (next) => {
      try {
        if (next === null || next === undefined) {
          const res = await DELETE_COMPANY_GOAL();
          const saved = normalizeCompanyGoal(res?.data);
          setGoalState(saved);
          broadcastGoal(companyId, saved);
          return saved;
        }
        const res = await SAVE_COMPANY_GOAL({
          kpiId: next.kpiId,
          target: Number(next.target),
          period: next.period || "month",
        });
        const saved = normalizeCompanyGoal(res?.data);
        setGoalState(saved);
        broadcastGoal(companyId, saved);
        return saved;
      } catch (e) {
        return null;
      }
    },
    [companyId],
  );

  const uploadMountainImage = useCallback(
    async (file) => {
      if (!file) return null;
      try {
        const formData = new FormData();
        formData.append("image", file);
        const res = await UPLOAD_COMPANY_GOAL_MOUNTAIN_IMAGE(formData);
        const saved = normalizeCompanyGoal(res?.data);
        setGoalState(saved);
        broadcastGoal(companyId, saved);
        return saved;
      } catch (e) {
        return null;
      }
    },
    [companyId],
  );

  const removeMountainImage = useCallback(
    async () => {
      try {
        const res = await DELETE_COMPANY_GOAL_MOUNTAIN_IMAGE();
        const saved = normalizeCompanyGoal(res?.data);
        setGoalState(saved);
        broadcastGoal(companyId, saved);
        return saved;
      } catch (e) {
        return null;
      }
    },
    [companyId],
  );

  return [goal, setGoal, uploadMountainImage, removeMountainImage];
};

/**
 * Sums a single KPI's `.amt` across every user's score for the given period,
 * across the whole company (every workspace). Matches the goal-mountain's
 * "company-wide goal" semantics, not the selected-workspace semantics.
 */
export const useCompanyKpiTotal = (kpiId, period = "month") => {
  const usersKpis = useSelector((state) => state.kpiReducer?.usersKpis);
  if (!kpiId || !Array.isArray(usersKpis)) return 0;
  return usersKpis.reduce((sum, u) => {
    const v = u?.[period]?.[kpiId]?.amt || 0;
    return sum + v;
  }, 0);
};

export const PERIOD_OPTIONS = [
  { value: "today", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

export const periodLabel = (value) =>
  PERIOD_OPTIONS.find((p) => p.value === value)?.label || "Monthly";
